/**
 * The four meal types, for the setup form. Issue #1106.
 *
 * ⚠️ THIS LIST IS DUPLICATED, ON PURPOSE, and pinned by a test.
 * The generation side needs the same four values and labels, but it lives in
 * `supabase/functions/run-scheduled-tasks/mealTypes.ts` — a Deno module with
 * URL imports and `.ts`-suffixed specifiers that Vite cannot bundle into the
 * app. Rather than contort either side into importing across that boundary,
 * both declare the list and `src/lib/__tests__/mealTypes.test.ts` asserts they
 * are identical. A drift there would show one thing in the setup form and
 * generate another, which is a silent, per-user wrong plan — so the test is the
 * mechanism, not the comment.
 */

export type MealType = "breakfast" | "my_lunch" | "school_lunch" | "dinner";

/** Canonical order — the order of the day, not the order they were shipped. */
export const MEAL_TYPES: readonly MealType[] = [
  "breakfast",
  "my_lunch",
  "school_lunch",
  "dinner",
] as const;

export const MEAL_TYPE_LABELS: Record<MealType, string> = {
  breakfast: "Breakfast",
  my_lunch: "My lunch",
  school_lunch: "School lunches",
  dinner: "Dinner",
};

export const DEFAULT_MEAL_TYPES: MealType[] = ["dinner"];

/**
 * Read a stored `meal_types` config into canonical values.
 *
 * Mirrors `normalizeMealTypes` in the Deno module, including the legacy map:
 * existing configs hold "Dinner" / "Lunch" / "Breakfast", and "Lunch" has no
 * canonical equivalent any more — it split into "My lunch" and "School
 * lunches". It maps to `my_lunch`, because that is what it meant when she chose
 * it (the school-lunch concept did not exist), and because inventing school
 * lunches for a household whose kids we have not asked about is worse than
 * under-delivering.
 */
export function normalizeMealTypes(raw: unknown): MealType[] {
  if (!Array.isArray(raw) || raw.length === 0) return [...DEFAULT_MEAL_TYPES];

  const out: MealType[] = [];
  for (const entry of raw) {
    if (typeof entry !== "string") continue;
    const key = entry.trim().toLowerCase().replace(/[\s-]+/g, "_");
    const mapped = LEGACY_MAP[key];
    if (mapped && !out.includes(mapped)) out.push(mapped);
  }

  if (out.length === 0) return [...DEFAULT_MEAL_TYPES];
  return MEAL_TYPES.filter((t) => out.includes(t));
}

const LEGACY_MAP: Record<string, MealType> = {
  breakfast: "breakfast",
  my_lunch: "my_lunch",
  school_lunch: "school_lunch",
  school_lunches: "school_lunch",
  dinner: "dinner",
  lunch: "my_lunch",
  dinners: "dinner",
};

/**
 * Which meal type this meal IS. Issue #1543.
 *
 * ⚠️ THE one fold in `src/`. An absent, empty or non-canonical `meal_type`
 * reads as `dinner` — where a pre-#1106 plan lives anyway — so a mis-tagged
 * meal is folded, never dropped.
 *
 * It lives here because `mealTypes.ts` has zero imports, so every reader can
 * reach it. It used to live in `mealFavorites.ts` (which pulls in the supabase
 * client) and was therefore COPIED into `mealPlanSwapRecipe.ts`, and a fifth
 * reader — `scopeMealsToType` — asked `typeof raw === "string"` instead of "is
 * it one of ours". That one kept `""` as its own pseudo-type, so a mis-tagged
 * meal was counted by the chip ("2 days planned") and then dropped by the
 * sheet it opened (1 row), with the hidden-types affordance silent because it
 * folded the same meal to `dinner`. The meal vanished with no trace.
 *
 * Every reader converges here. Do not write a second copy.
 */
export function mealTypeOf(meal: { meal_type?: string | null } | null | undefined): MealType {
  const raw = meal?.meal_type;
  return (MEAL_TYPES as readonly string[]).includes(raw ?? "") ? (raw as MealType) : "dinner";
}
